import Vector2 from "../math/Vector2";
import isMobile from "ismobilejs";

/**
 * @class
 * @memberOf P3
 */
export default class Input {
    constructor() {
        /**
         * @member {object} signals
         * @member {signals.Signal} signals.mouseMove
         * @member {signals.Signal} signals.mouseDown
         * @member {signals.Signal} signals.mouseUp
         * @member {signals.Signal} signals.mouseClick
         * @member {signals.Signal} signals.keyDown
         * @member {signals.Signal} signals.keyUp
         * @member {signals.Signal} signals.swipeLeft
         * @member {signals.Signal} signals.swipeRight
         * @member {signals.Signal} signals.swipeUp
         * @member {signals.Signal} signals.swipeDown
         * @member {signals.Signal} signals.orientation
         */
        this.signals = {
            mouseMove: new signals.Signal(),
            mouseDown: new signals.Signal(),
            mouseUp: new signals.Signal(),
            mouseClick: new signals.Signal(),
            keyDown: new signals.Signal(),
            keyUp: new signals.Signal(),
            swipeLeft: new signals.Signal(),
            swipeRight: new signals.Signal(),
            swipeUp: new signals.Signal(),
            swipeDown: new signals.Signal(),
            orientation: new signals.Signal()
        };

        /**
         * The element listening for key strokes will be focused when the document is clicked.
         * @member {boolean}
         */
        this.refocus = true;

        /**
         * @member {PIXI.DisplayObject}
         * @private
         */
        this._stage = null;

        /**
         * @member {*}
         * @private
         */
        this._keyListener = null;

        /**
         * @member {Object}
         * @private
         */
        this._keyMap = {};

        /**
         * @member {Object}
         * @private
         */
        this._keyBuffer = {};

        /**
         * @member {PIXI.Point}
         * @private
         */
        this._touch = null;

        /**
         * @member {null}
         * @private
         */
        this._swipeStart = null;

        /**
         * @member {number}
         * @private
         */
        this._swipeTime = 0;

        /**
         * @member {Object} orientation
         * @member {number} orientation.gamma
         * @member {number} orientation.beta
         * @member {number} orientation.alpha
         * @private
         */
        this._orientation = {
            gamma: 0,
            beta: 0,
            alpha: 0
        };

        /**
         * @member
         * @private
         */
        this._func = {};
    }

    /**
     * @param {PIXI.DisplayObject} stage
     * @param {*} keyListener
     */
    init(stage, keyListener) {
        this._stage = stage;
        this._stage.interactive = true;
        this._stage.on("mousemove", this.onMouseMove, this);
        this._stage.on("mousedown", this.onMouseDown, this);
        this._stage.on("mouseup", this.onMouseUp, this);
        this._stage.on("click", this.onMouseClick, this);
        this._stage.on("touchmove", this.onMouseMove, this);
        this._stage.on("touchstart", this.onMouseDown, this);
        this._stage.on("touchend", this.onMouseUp, this);
        this._stage.on("tap", this.onMouseClick, this);

        this._keyListener = keyListener;
        this._keyListener.addEventListener("keydown", this._func.keydown = this.onKeyDown.bind(this));
        this._keyListener.addEventListener("keyup", this._func.keyup = this.onKeyUp.bind(this));
        this._keyListener.focus();

        document.addEventListener("click", this._func.click = function() {
            this.refocus && this._keyListener.focus();
        }.bind(this));

        isMobile.any && window.addEventListener("deviceorientation", this._func.deviceorientation = this.onDeviceOrientation.bind(this));
    }

    destroy() {
        this._stage.off("mousemove", this.onMouseMove, this);
        this._stage.off("mousedown", this.onMouseDown, this);
        this._stage.off("mouseup", this.onMouseUp, this);
        this._stage.off("click", this.onMouseClick, this);
        this._stage.off("touchmove", this.onMouseMove, this);
        this._stage.off("touchstart", this.onMouseDown, this);
        this._stage.off("touchend", this.onMouseUp, this);
        this._stage.off("tap", this.onMouseClick, this);

        this._keyListener.removeEventListener("keydown", this._func.keydown);
        this._keyListener.removeEventListener("keyup", this._func.keyup);

        document.removeEventListener("click", this._func.click);

        isMobile.any && window.removeEventListener("deviceorientation", this._func.deviceorientation);

        this.signals.mouseMove.dispose();
        this.signals.mouseDown.dispose();
        this.signals.mouseUp.dispose();
        this.signals.mouseClick.dispose();

        this.signals.keyDown.dispose();
        this.signals.keyUp.dispose();

        this.signals.swipeLeft.dispose();
        this.signals.swipeRight.dispose();
        this.signals.swipeUp.dispose();
        this.signals.swipeDown.dispose();

        this.signals.orientation.dispose();
    }

    /**
     */
    update() {
        for (let i in this._keyBuffer) {
            if (this._keyBuffer.hasOwnProperty(i)) {
                this._keyBuffer[i] = false;
            }
        }
    }

    /**
     * @param {number} keyCode
     * @returns {boolean}
     */
    isKeyDown(keyCode) {
        return this._keyMap[keyCode] === true;
    }

    /**
     * @param {number} keyCode
     * @returns {boolean}
     */
    isKeyPressed(keyCode) {
        return this._keyBuffer[keyCode] === true;
    }

    /**
     * @param {Event} event
     */
    onMouseMove(event) {
        this._touch && (this._touch = event.data.getLocalPosition(this._stage));

        this.signals.mouseMove.dispatch(event);
    }

    /**
     * @param {Event} event
     */
    onMouseDown(event) {
        this._touch = event.data.getLocalPosition(this._stage);

        if (isMobile.any) {
            this._swipeStart = this._touch.clone();
            this._swipeTime = window.performance.now();
        }
        this.signals.mouseDown.dispatch(event);
    }

    /**
     * @param {Event} event
     */
    onMouseUp(event) {
        if (isMobile.any) {
            const swipe = new Vector2(this._touch.x - this._swipeStart.x, this._touch.y - this._swipeStart.y);
            const distance = swipe.lengthSq;
            const minimum = 4 * 4;
            swipe.normalize();

            if (window.performance.now() - this._swipeTime < 800 && distance > minimum) {
                const left = swipe.dot(new Vector2(-1, 0));
                (left > 0.9) && this.signals.swipeLeft.dispatch();

                const right = swipe.dot(new Vector2(1, 0));
                (right > 0.9) && this.signals.swipeRight.dispatch();

                const up = swipe.dot(new Vector2(0, -1));
                (up > 0.9) && this.signals.swipeUp.dispatch();

                const down = swipe.dot(new Vector2(0, 1));
                (down > 0.9) && this.signals.swipeDown.dispatch();
            }
        }

        this._touch = null;
        this.signals.mouseUp.dispatch(event);
    }

    /**
     * @param {Event} event
     */
    onMouseClick(event) {
        this.signals.mouseClick.dispatch(event);
    }

    /**
     * @param {Event} event
     */
    onKeyDown(event) {
        if (event.keyCode === Input.keys.LEFT || event.keyCode === Input.keys.RIGHT ||
            event.keyCode === Input.keys.UP || event.keyCode === Input.keys.DOWN ||
            event.keyCode === Input.keys.SPACE) event.preventDefault();

        !this.isKeyDown(event.keyCode) && (this._keyBuffer[event.keyCode] = true);
        this._keyMap[event.keyCode] = true;
        this.signals.keyDown.dispatch(event);
    }

    /**
     * @param {Event} event
     */
    onKeyUp(event) {
        if (event.keyCode === Input.keys.LEFT || event.keyCode === Input.keys.RIGHT ||
            event.keyCode === Input.keys.UP || event.keyCode === Input.keys.DOWN ||
            event.keyCode === Input.keys.SPACE) event.preventDefault();

        this._keyMap[event.keyCode] = false;
        this.signals.keyUp.dispatch(event);
    }

    /**
     * @param {Event} event
     */
    onDeviceOrientation(event) {
        this.signals.orientation.dispatch(event);
    }

    /**
     * @returns {PIXI.Point}
     */
    get touch() {
        return this._touch;
    }

    /**
     * @returns {Object} orientation
     * @returns {number} orientation.gamma
     * @returns {number} orientation.beta
     * @returns {number} orientation.alpha
     */
    get orientation() {
        return this._orientation;
    }
}

/**
 * @static
 * @constant
 * @type {Object}
 */
Input.keys = {
    TAB: 9,
    ENTER: 13,
    SHIFT: 16,
    CTRL: 17,
    SPACE: 32,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    NUM_0: 48,
    NUM_1: 49,
    NUM_2: 50,
    NUM_3: 51,
    NUM_4: 52,
    NUM_5: 53,
    NUM_6: 54,
    NUM_7: 55,
    NUM_8: 56,
    NUM_9: 57,
    A: 65,
    B: 66,
    C: 67,
    D: 68,
    E: 69,
    F: 70,
    G: 71,
    H: 72,
    I: 73,
    J: 74,
    K: 75,
    L: 76,
    M: 77,
    N: 78,
    O: 79,
    P: 80,
    Q: 81,
    R: 82,
    S: 83,
    T: 84,
    U: 85,
    V: 86,
    W: 87,
    X: 88,
    Y: 89,
    Z: 90,
    PLUS: 187,
    MINUS: 189
};
